import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { CSSProperties, MouseEvent, ReactNode } from 'react'
import { ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { Link } from 'react-router-dom'

import type { NavigationIcon } from '@/components/progetto_arca/LumaIcon'
import { brandAssetUrl } from '@/runtime'
import { useStoredFlag } from '@/shell/useStoredFlag'
import { cn } from '@/lib/utils'
import styles from './DesignerSidebar.module.scss'

const COLLAPSED_WIDTH = 52
const DEFAULT_WIDTH = 292
const MIN_EXPANDED_WIDTH = 232
const COLLAPSE_THRESHOLD = 100
// v2 retires the old 380px default without overriding widths chosen in this
// compact shell.
const SIDEBAR_WIDTH_KEY = 'progetto_arca.sidebar.width.v2'

function maxExpandedWidth(): number {
  return Math.max(DEFAULT_WIDTH, window.innerWidth * 0.3)
}

function clampExpandedWidth(width: number): number {
  return Math.max(MIN_EXPANDED_WIDTH, Math.min(width, maxExpandedWidth()))
}

function persistExpandedWidth(width: number) {
  try {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, String(Math.round(width)))
  } catch {
    /* localStorage unavailable — width just won't persist */
  }
}

interface SidebarContextValue {
  collapsed: boolean
  width: number
  isDragging: boolean
  isTransitioning: boolean
  toggle: () => void
  startResize: (event: MouseEvent) => void
  resizeBy: (delta: number) => void
}

const SidebarContext = createContext<SidebarContextValue>({
  collapsed: false,
  width: DEFAULT_WIDTH,
  isDragging: false,
  isTransitioning: false,
  toggle: () => {},
  startResize: () => {},
  resizeBy: () => {},
})

export interface DesignerSidebarUser {
  initials: string
  displayName: string
  href: string
  active: boolean
}

export interface DesignerSidebarItem {
  id?: string
  href?: string
  icon: NavigationIcon
  label: string
  statusLabel?: string
  title?: string
  active: boolean
  end?: boolean
  /**
   * When true, paint a small red dot on the top-right of the icon to
   * signal that someone else has pushed new items behind this route.
   * Purely presentational — the link still navigates to `href` and the
   * screen-reader text is appended to the label. Do NOT set this from a
   * generic "has items" count; the dot is reserved for an unread /
   * pushed-by-other signal.
   */
  hasNotification?: boolean
  /**
   * Optional number to render in a small pill next to the label when the
   * sidebar is expanded. Used today as a steady-state count badge. Independent of `hasNotification` —
   * a pill can exist without a dot, and a dot without a pill.
   */
  notificationCount?: number
}

export interface DesignerSidebarGroup {
  label: string
  items: DesignerSidebarItem[]
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useStoredFlag('progetto_arca.sidebar.collapsed', false)
  const [width, setWidth] = useState(() => {
    try {
      const stored = Number(localStorage.getItem(SIDEBAR_WIDTH_KEY))
      return Number.isFinite(stored) && stored >= MIN_EXPANDED_WIDTH
        ? clampExpandedWidth(stored)
        : DEFAULT_WIDTH
    } catch {
      return DEFAULT_WIDTH
    }
  })
  const [isDragging, setIsDragging] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const transitionTimer = useRef<number | null>(null)

  const toggle = useCallback(() => {
    // Set the transitioning flag in the same render as the collapsed flip so
    // React batches both state changes. This prevents a single-frame glitch
    // where data-collapsed='true' is committed but pointer-events: none is
    // not yet applied — that frame is where the cursor still over the toggle
    // button would trigger the collapsed :hover rule on the logo.
    setIsTransitioning(true)
    if (transitionTimer.current !== null) {
      window.clearTimeout(transitionTimer.current)
    }
    transitionTimer.current = window.setTimeout(() => {
      setIsTransitioning(false)
      transitionTimer.current = null
    }, 250)

    if (collapsed) {
      setWidth((previous) => (previous <= COLLAPSED_WIDTH ? DEFAULT_WIDTH : previous))
    }
    setCollapsed(!collapsed)
  }, [collapsed, setCollapsed])

  useEffect(() => {
    return () => {
      if (transitionTimer.current !== null) {
        window.clearTimeout(transitionTimer.current)
      }
    }
  }, [])

  const startResize = useCallback((event: MouseEvent) => {
    event.preventDefault()
    setIsDragging(true)

    const onMouseMove = (moveEvent: globalThis.MouseEvent) => {
      const nextWidth = moveEvent.clientX
      if (nextWidth < COLLAPSE_THRESHOLD) {
        setCollapsed(true)
        setWidth(COLLAPSED_WIDTH)
        return
      }

      setCollapsed(false)
      setWidth(clampExpandedWidth(nextWidth))
    }

    const onMouseUp = () => {
      setIsDragging(false)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      // Persist the dragged width (expanded widths only — a drag-to-collapse
      // leaves the last expanded width in storage for the next expand).
      setWidth((current) => {
        if (current >= MIN_EXPANDED_WIDTH) {
          persistExpandedWidth(current)
        }
        return current
      })
    }

    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [])

  const resizeBy = useCallback(
    (delta: number) => {
      setCollapsed(false)
      setWidth((current) => {
        const next = clampExpandedWidth(
          current <= COLLAPSED_WIDTH ? DEFAULT_WIDTH : current + delta,
        )
        persistExpandedWidth(next)
        return next
      })
    },
    [setCollapsed],
  )

  return (
    <SidebarContext.Provider
      value={{ collapsed, width, isDragging, isTransitioning, toggle, startResize, resizeBy }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  return useContext(SidebarContext)
}

export function DesignerSidebar({
  chatSection,
  footerAction,
  groups,
  homeLabel,
  logo,
  showGroupHeaders = true,
  utilityItem,
  user,
}: {
  chatSection?: ReactNode
  footerAction?: ReactNode
  groups: DesignerSidebarGroup[]
  homeLabel: string
  logo?: ReactNode
  showGroupHeaders?: boolean
  utilityItem?: DesignerSidebarItem
  user?: DesignerSidebarUser | null
}) {
  const { collapsed, width, isDragging, isTransitioning, toggle, startResize, resizeBy } =
    useSidebar()
  const visibleGroups = groups.filter((group) => group.items.length > 0)

  return (
    <aside
      className={cn(styles.sidebar, !isDragging && styles.sidebarAnimated)}
      data-collapsed={collapsed ? 'true' : 'false'}
      style={
        {
          width: collapsed ? COLLAPSED_WIDTH : width,
          // Content lays out at the destination width while the aside tweens, so
          // labels never rewrap mid-animation (see .inner in the module).
          '--sidebar-width': `${collapsed ? COLLAPSED_WIDTH : width}px`,
          pointerEvents: isTransitioning ? 'none' : undefined,
        } as CSSProperties
      }
    >
      <div className={styles.inner}>
        <div className={styles.header}>
          {collapsed ? (
            <button
              type="button"
              onClick={toggle}
              className={styles.logoExpandButton}
              aria-label="Espandi menu"
              aria-expanded={false}
              title="Espandi menu"
            >
              <span className={styles.collapsedLogoMark}>
                {logo ?? <DefaultLogoMark collapsed />}
              </span>
              <PanelLeftOpen className={styles.collapsedExpandIcon} size={16} aria-hidden />
            </button>
          ) : (
            <>
              <Link to="/" aria-label={homeLabel} className={styles.logoLink}>
                {logo ?? <DefaultLogoMark collapsed={false} />}
              </Link>
              <button
                type="button"
                onClick={toggle}
                className={styles.iconButton}
                aria-label="Comprimi menu"
                aria-expanded
                title="Comprimi menu"
              >
                <PanelLeftClose size={16} aria-hidden />
              </button>
            </>
          )}
        </div>

        <nav className={styles.nav} aria-label="Principale">
          {visibleGroups.map((group) => (
            <SidebarGroup key={group.label} label={group.label} showHeader={showGroupHeaders}>
              {group.items.map((item) => (
                <SidebarItem key={item.id ?? item.href} item={item} />
              ))}
            </SidebarGroup>
          ))}
          {chatSection}
        </nav>

        {(utilityItem || user || footerAction) && (
          <div className={styles.footer}>
            {utilityItem ? (
              <div className={styles.utilityItem}>
                <SidebarItem item={utilityItem} />
              </div>
            ) : null}
            {user || footerAction ? (
              <div className={styles.footerRow}>
                {user ? <UserRow user={user} /> : null}
                {footerAction ? <div className={styles.footerAction}>{footerAction}</div> : null}
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Ridimensiona navigazione"
        aria-valuemin={collapsed ? COLLAPSED_WIDTH : MIN_EXPANDED_WIDTH}
        aria-valuemax={Math.round(maxExpandedWidth())}
        aria-valuenow={Math.round(collapsed ? COLLAPSED_WIDTH : width)}
        tabIndex={0}
        className={styles.resizeHandle}
        onMouseDown={startResize}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
          event.preventDefault()
          if (collapsed && event.key === 'ArrowLeft') return
          const step = event.shiftKey ? 40 : 16
          resizeBy(collapsed ? 0 : event.key === 'ArrowLeft' ? -step : step)
        }}
      />
    </aside>
  )
}

function DefaultLogoMark({ collapsed }: { collapsed: boolean }) {
  return (
    <>
      <span className={styles.logoVisual} aria-hidden>
        <img
          src={brandAssetUrl('progetto-arca-symbol-white.png')}
          alt=""
          className={styles.logoMark}
          draggable={false}
        />
        {!collapsed ? <span className={styles.logoText}>Progetto Arca</span> : null}
      </span>
      <span className={styles.srOnly}>Progetto Arca</span>
    </>
  )
}

function SidebarGroup({
  children,
  label,
  showHeader,
}: {
  children: ReactNode
  label: string
  showHeader: boolean
}) {
  const { collapsed } = useSidebar()
  const [isOpen, setIsOpen] = useState(true)

  useEffect(() => {
    if (!collapsed) setIsOpen(true)
  }, [collapsed])

  const showChildren = collapsed || isOpen

  return (
    <section className={cn(styles.group, !showHeader && styles.groupFlat)}>
      {showHeader ? (
        <button
          type="button"
          className={styles.groupButton}
          onClick={collapsed ? undefined : () => setIsOpen((open) => !open)}
          aria-expanded={collapsed ? undefined : isOpen}
          aria-label={label}
        >
          {collapsed ? (
            <span className={styles.collapsedRule} aria-hidden />
          ) : (
            <>
              <span className={styles.groupLabel}>{label}</span>
              <ChevronRight
                className={cn(styles.groupChevron, isOpen && styles.groupChevronOpen)}
                aria-hidden
              />
            </>
          )}
        </button>
      ) : null}
      {showChildren ? <div className={styles.groupItems}>{children}</div> : null}
    </section>
  )
}

function SidebarItem({ item }: { item: DesignerSidebarItem }) {
  const { collapsed } = useSidebar()
  const Icon = item.icon
  const label = item.statusLabel ? `${item.label}, ${item.statusLabel}` : item.label
  const content = (
    <>
      <span className={styles.itemIconWrap}>
        <Icon className={styles.itemIcon} aria-hidden />
        {item.hasNotification ? <span className={styles.itemNotificationDot} aria-hidden /> : null}
      </span>
      {!collapsed ? (
        <span className={styles.itemLabelRow}>
          <span className={styles.itemLabel}>{item.label}</span>
          {item.statusLabel ? <span className={styles.itemStatus}>{item.statusLabel}</span> : null}
          {item.notificationCount != null && item.notificationCount > 0 ? (
            <span className={styles.itemNotificationCount} aria-hidden>
              {item.notificationCount}
            </span>
          ) : null}
        </span>
      ) : null}
      {item.hasNotification ? (
        <span className={styles.srOnly}>
          {item.notificationCount != null && item.notificationCount > 0
            ? `(${item.notificationCount} nuovi elementi)`
            : '(elementi non letti)'}
        </span>
      ) : null}
    </>
  )

  if (!item.href) {
    if (!item.statusLabel) return null
    return (
      <div
        title={item.title}
        aria-label={label}
        aria-disabled="true"
        className={cn(styles.item, styles.itemDisabled)}
      >
        {content}
      </div>
    )
  }

  return (
    <Link
      to={item.href}
      title={collapsed ? item.title || item.label : item.title}
      aria-label={label}
      aria-current={item.active ? 'page' : undefined}
      className={cn(styles.item, item.active && styles.itemActive)}
    >
      {content}
    </Link>
  )
}

function UserRow({ user }: { user: DesignerSidebarUser }) {
  const { collapsed } = useSidebar()

  return (
    <Link
      to={user.href}
      aria-label={user.displayName}
      aria-current={user.active ? 'page' : undefined}
      className={cn(styles.user, user.active && styles.userActive)}
      title={collapsed ? user.displayName : undefined}
    >
      <span className={styles.avatar} aria-hidden>
        {user.initials}
      </span>
      {!collapsed ? (
        <span className={styles.userText}>
          <span className={styles.userName}>{user.displayName}</span>
        </span>
      ) : null}
    </Link>
  )
}
